import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { automationAPI } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import { 
  Zap, Play, Clock, CheckCircle, RefreshCw, Eye, 
  AlertTriangle, Settings, Shield, Bug, Globe, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const getCategoryColor = (category) => {
  const colors = {
    incident_response: 'bg-destructive/20 text-destructive border-destructive/30',
    threat_response: 'bg-warning/20 text-warning border-warning/30',
    prevention: 'bg-success/20 text-success border-success/30',
    vulnerability_management: 'bg-info/20 text-info border-info/30',
  };
  return colors[category] || 'bg-muted text-muted-foreground';
};

const getStepIcon = (type) => {
  const icons = {
    nac_action: Shield,
    firewall_rule: Shield,
    notification: Bell,
    forensics: Eye,
    backup: Settings,
    scan: Bug,
    user_lookup: Globe,
    url_block: Shield,
    auth_action: Settings,
    analysis: Eye,
    logging: CheckCircle,
    scheduling: Clock,
    patch_management: RefreshCw,
  };
  return icons[type] || Zap;
};

export function AutomationPage() {
  const [playbooks, setPlaybooks] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, total_executions_today: 0, success_rate: 98 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [executing, setExecuting] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await automationAPI.getPlaybooks();
      setPlaybooks(res.data.playbooks || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error('Error loading playbooks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecute = async (playbookId) => {
    setExecuting(playbookId);
    try {
      const res = await automationAPI.executePlaybook(playbookId);
      if (res.data.success) {
        toast.success(`Playbook executado com sucesso! ${res.data.execution.steps_completed} passos concluídos.`);
        loadData();
        if (selectedPlaybook?.id === playbookId) {
          setSelectedPlaybook(prev => prev ? { ...prev, executions: (prev.executions || 0) + 1 } : prev);
        }
      }
    } catch (error) {
      toast.error('Erro ao executar playbook');
    } finally {
      setExecuting(null);
    }
  };

  return (
    <div className="min-h-screen" data-testid="automation-page">
      <Header title="Automation (SOAR)" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50 border-border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Playbooks</p>
                  <p className="text-3xl font-mono font-bold">{stats.total}</p>
                </div>
                <Zap className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-success/10 border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Active</p>
                  <p className="text-3xl font-mono font-bold text-success">{stats.active}</p>
                </div>
                <Play className="h-8 w-8 text-success opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-info/10 border-info/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Execuções</p>
                  <p className="text-3xl font-mono font-bold text-info">{stats.total_executions_today}</p>
                </div>
                <Clock className="h-8 w-8 text-info opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-success/10 border-success/30">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Taxa de Sucesso</p>
                  <p className="text-3xl font-mono font-bold text-success">{stats.success_rate}%</p>
                </div>
                <CheckCircle className="h-8 w-8 text-success opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Playbooks Grid */}
        <Card className="bg-card/50 border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
              Playbooks de Automação
            </CardTitle>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {playbooks.map((playbook) => (
                <div
                  key={playbook.id}
                  className="p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedPlaybook(playbook)}
                  data-testid={`playbook-${playbook.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded bg-primary/20">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{playbook.name}</p>
                        <Badge className={cn("text-xs border mt-1", getCategoryColor(playbook.category))}>
                          {playbook.category?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    <Badge variant={playbook.status === 'active' ? 'default' : 'secondary'}>
                      {playbook.status}
                    </Badge>
                  </div>

                  <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{playbook.description}</p>

                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span>{playbook.steps?.length || 0} passos</span>
                    <span>{playbook.executions || 0} execuções</span>
                    <span>{playbook.last_run ? `Último: ${formatDate(playbook.last_run)}` : 'Nunca executado'}</span>
                  </div>

                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      className="flex-1"
                      disabled={playbook.status !== 'active' || executing === playbook.id}
                      onClick={() => handleExecute(playbook.id)}
                      data-testid={`execute-playbook-${playbook.id}`}
                    >
                      {executing === playbook.id ? (
                        <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-1" />
                      )}
                      Executar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedPlaybook(playbook)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Playbook Detail Modal */}
      <Dialog open={!!selectedPlaybook} onOpenChange={() => setSelectedPlaybook(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Zap className="h-6 w-6 text-primary" />
              <span>{selectedPlaybook?.name}</span>
              <Badge variant={selectedPlaybook?.status === 'active' ? 'default' : 'secondary'}>
                {selectedPlaybook?.status}
              </Badge>
              {selectedPlaybook && (
                <Badge className={cn("border text-xs", getCategoryColor(selectedPlaybook.category))}>
                  {selectedPlaybook.category?.replace('_', ' ')}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedPlaybook && (
            <ScrollArea className="h-[550px]">
              <div className="space-y-4 mt-2 pr-2">
                <Card className="bg-secondary/50">
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground">{selectedPlaybook.description}</p>
                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-background rounded">
                        <p className="text-2xl font-mono font-bold">{selectedPlaybook.steps?.length || 0}</p>
                        <p className="text-xs text-muted-foreground">Passos</p>
                      </div>
                      <div className="text-center p-3 bg-background rounded">
                        <p className="text-2xl font-mono font-bold text-info">{selectedPlaybook.executions || 0}</p>
                        <p className="text-xs text-muted-foreground">Execuções</p>
                      </div>
                      <div className="text-center p-3 bg-background rounded">
                        <p className="text-2xl font-mono font-bold text-success">98%</p>
                        <p className="text-xs text-muted-foreground">Sucesso</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div>
                  <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4" /> Passos do Playbook
                  </h4>
                  <div className="space-y-3">
                    {selectedPlaybook.steps?.map((step, i) => {
                      const Icon = getStepIcon(step.type);
                      return (
                        <div key={step.id} className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm border border-border">
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                              {i + 1}
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <span className="font-medium text-sm">{step.name}</span>
                              <Badge variant="outline" className="text-xs font-mono">{step.type}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{step.description}</p>
                          </div>
                          <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {selectedPlaybook.last_run && (
                  <Card className="bg-secondary/50">
                    <CardContent className="pt-4">
                      <h4 className="font-display text-xs uppercase text-muted-foreground mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" /> Última Execução
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Data/Hora</span>
                          <span className="font-mono text-sm">{formatDate(selectedPlaybook.last_run)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Status</span>
                          <Badge className="bg-success/20 text-success border-success/30 border">COMPLETED</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Passos concluídos</span>
                          <span className="font-mono text-sm">{selectedPlaybook.steps?.length || 0}/{selectedPlaybook.steps?.length || 0}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1"
                    disabled={selectedPlaybook.status !== 'active' || executing === selectedPlaybook.id}
                    onClick={() => handleExecute(selectedPlaybook.id)}
                    data-testid="execute-playbook-modal-btn"
                  >
                    {executing === selectedPlaybook.id ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-2" />
                    )}
                    Executar Agora
                  </Button>
                  <Button variant="outline">
                    <Settings className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AutomationPage;
